import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFAQs = () => {
  const { token } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({ question: '', answer: '', category: 'general', order: 0 });

  useEffect(() => { fetchFaqs(); }, []);

  const fetchFaqs = async () => {
    try { const res = await axios.get(`${API_URL}/api/faqs`); setFaqs(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingFaq) { await axios.put(`${API_URL}/api/faqs/${editingFaq.id}`, formData, { headers }); toast.success('FAQ updated!'); }
      else { await axios.post(`${API_URL}/api/faqs`, formData, { headers }); toast.success('FAQ added!'); }
      setDialogOpen(false); resetForm(); fetchFaqs();
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try { await axios.delete(`${API_URL}/api/faqs/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deleted!'); fetchFaqs(); }
    catch (e) { toast.error('Failed'); }
  };

  const openEdit = (faq) => { setEditingFaq(faq); setFormData(faq); setDialogOpen(true); };
  const resetForm = () => { setEditingFaq(null); setFormData({ question: '', answer: '', category: 'general', order: 0 }); };

  return (
    <AdminLayout title="FAQs">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">Manage frequently asked questions.</p>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button data-testid="add-faq-btn"><Plus className="w-4 h-4 mr-2" /> Add FAQ</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingFaq ? 'Edit' : 'Add'} FAQ</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label>Question</Label><Input value={formData.question} onChange={(e) => setFormData({...formData, question: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Answer</Label><Textarea value={formData.answer} onChange={(e) => setFormData({...formData, answer: e.target.value})} rows={4} required /></div>
              <div className="space-y-2"><Label>Category</Label><Input value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} /></div>
              <Button type="submit" className="w-full">{editingFaq ? 'Update' : 'Add'} FAQ</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (<div className="space-y-4">{[1,2,3].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-24"></CardContent></Card>)}</div>) : faqs.length > 0 ? (
        <div className="space-y-4">
          {faqs.map((faq) => (
            <Card key={faq.id} data-testid={`faq-${faq.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><HelpCircle className="w-5 h-5 text-primary" /></div>
                  <div className="flex-1"><h4 className="font-semibold">{faq.question}</h4><p className="text-sm text-muted-foreground mt-1 line-clamp-2">{faq.answer}</p></div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => openEdit(faq)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="outline" size="icon" onClick={() => handleDelete(faq.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (<Card><CardContent className="py-16 text-center"><HelpCircle className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold">No FAQs</h3></CardContent></Card>)}
    </AdminLayout>
  );
};

export default AdminFAQs;
